// eBarimt API Helper Functions
// Handles both 7-digit and 8-digit registration codes

export interface EBarimtCompanyInfo {
  name?: string;
  tin?: string;
  aimagName?: string;
  vatName?: string;
  mainSector?: string;
  contactName?: string;
  vatPayer?: boolean;
  cityPayer?: boolean;
  directorFname?: string;
  directorLname?: string;
  directorRegno?: string;
  address?: string;
}

/**
 * Validates and formats registration number for eBarimt API
 * Supports 7-digit, 8-digit, and 10-digit registration codes
 * Handles registration numbers with letters/prefixes
 */
export const formatRegistrationNumber = (regNo: string): string => {
  // For registration numbers with letters (like ЧИ87050973), use the full string
  // For pure numeric registration numbers, use as is
  const hasLetters = /[A-Za-zА-Яа-я]/.test(regNo);
  
  if (hasLetters) {
    // If it contains letters, use the full registration number
    console.log('🔥 Registration number with letters detected:', regNo);
    return regNo;
  } else {
    // For pure numeric registration numbers, validate length
    const cleanRegNo = regNo.replace(/\D/g, '');
    
    // Check if it's 7, 8, or 10 digits
    if (cleanRegNo.length === 7) {
      console.log('🔥 7-digit registration number detected:', cleanRegNo);
      return cleanRegNo;
    } else if (cleanRegNo.length === 8) {
      console.log('🔥 8-digit registration number detected:', cleanRegNo);
      return cleanRegNo;
    } else if (cleanRegNo.length === 10) {
      console.log('🔥 10-digit registration number detected:', cleanRegNo);
      return cleanRegNo;
    } else {
      console.log('🔥 Invalid registration number length:', cleanRegNo.length);
      throw new Error(`Invalid registration number length: ${cleanRegNo.length}. Expected 7, 8, or 10 digits.`);
    }
  }
};

/**
 * Gets TIN from eBarimt API using registration number
 * Handles both persons and companies with 7, 8, and 10-digit registration codes
 */
export const getTinFromRegistration = async (regNo: string): Promise<string | null> => {
  try {
    const formattedRegNo = formatRegistrationNumber(regNo);
    
    console.log('🔥 Fetching TIN from eBarimt API for regNo:', formattedRegNo);
    
    const response = await fetch(`https://api.ebarimt.mn/api/info/check/getTinInfo?regNo=${formattedRegNo}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    const tin = data?.data;
    
    if (tin) {
      console.log('🔥 TIN found:', tin);
      return tin;
    }
    
    console.log('🔥 No TIN found for registration number:', formattedRegNo);
    return null;
  } catch (error) {
    console.error('🔥 Error getting TIN from eBarimt API:', error);
    throw error;
  }
};

/**
 * Gets company information from eBarimt API using TIN
 */
export const getCompanyInfoFromTin = async (tin: string): Promise<EBarimtCompanyInfo | null> => {
  try {
    console.log('🔥 Fetching company info from eBarimt API for TIN:', tin);
    
    const response = await fetch(`https://api.ebarimt.mn/api/info/check/getInfo?tin=${tin}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    const companyInfo = data?.data;
    
    if (!companyInfo) {
      console.log('🔥 No company info found for TIN:', tin);
      return null;
    }
    
    console.log('🔥 Company info found:', companyInfo);
    return companyInfo;
  } catch (error) {
    console.error('🔥 Error getting company info from eBarimt API:', error);
    throw error;
  }
};

/**
 * Complete eBarimt lookup function
 * Uses the simple and reliable approach: regNo → TIN → Company Info
 * Handles both persons and companies with 7, 8, and 10-digit registration codes
 */
export const lookupCompanyFromRegistration = async (regNo: string): Promise<EBarimtCompanyInfo | null> => {
  try {
    console.log('🔥 Starting eBarimt lookup for registration number:', regNo);
    const formattedRegNo = formatRegistrationNumber(regNo);
    
    // Use local EBarimt API instead of external API
    const API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:9002';
    console.log('🔥 Using local EBarimt API:', `${API_BASE_URL}/api/check-ebarimt`);
    const response = await fetch(`${API_BASE_URL}/api/check-ebarimt?regno=${formattedRegNo}`);
    const result = await response.json();
    
    console.log('🔥 Local EBarimt API response:', result);
    
    if (!result.success || !result.data) {
      console.log('🔥 No data found from local EBarimt API');
      return null;
    }
    
    const ebarimtData = result.data;
    console.log('🔥 EBarimt data:', ebarimtData);
    
    // Transform the data to match EBarimtCompanyInfo interface
    const companyInfo: EBarimtCompanyInfo = {
      name: result.name || ebarimtData.name || '',
      tin: result.tin || '',
      aimagName: ebarimtData.aimagName || '',
      vatName: ebarimtData.vatName || '',
      mainSector: ebarimtData.mainSector || '',
      contactName: ebarimtData.contactName || '',
      vatPayer: ebarimtData.vatPayer || false,
      cityPayer: ebarimtData.cityPayer || false,
      directorFname: ebarimtData.directorLastName || '',
      directorLname: ebarimtData.directorName || '',   
      directorRegno: ebarimtData.directorRegno || '',
      address: ebarimtData.address || ''
    };
    
    console.log('🔥 Transformed company info:', companyInfo);
    return companyInfo;
  } catch (error) {
    console.error('🔥 Error in eBarimt lookup:', error);
    throw error;
  }
};

/**
 * Validates registration number format (for companies and individuals)
 */
export const isValidRegistrationNumber = (regNo: string): boolean => {
  // Check if it contains letters (like ЧИ87050973)
  const hasLetters = /[A-Za-zА-Яа-я]/.test(regNo);
  
  if (hasLetters) {
    // If it contains letters, consider it valid (for individuals)
    return true;
  } else {
    // For pure numeric registration numbers, validate length
    const cleanRegNo = regNo.replace(/\D/g, '');
    return cleanRegNo.length === 7 || cleanRegNo.length === 8 || cleanRegNo.length === 10;
  }
};

/**
 * Validates individual registration number format (for director registration)
 * Only accepts registration numbers with letters (individuals)
 */
export const isValidIndividualRegistrationNumber = (regNo: string): boolean => {
  // Only accept registration numbers with letters (individuals)
  const hasLetters = /[A-Za-zА-Яа-я]/.test(regNo);
  
  if (hasLetters) {
    // Must contain letters to be a valid individual registration number
    return true;
  } else {
    // Pure numeric registration numbers are for companies, not individuals
    return false;
  }
};

/**
 * Gets registration number length type
 */
export const getRegistrationNumberType = (regNo: string): '7-digit' | '8-digit' | '10-digit' | 'with-letters' | 'invalid' => {
  // Check if it contains letters (like ЧИ87050973)
  const hasLetters = /[A-Za-zА-Яа-я]/.test(regNo);
  
  if (hasLetters) {
    return 'with-letters';
  } else {
    const cleanRegNo = regNo.replace(/\D/g, '');
    if (cleanRegNo.length === 7) return '7-digit';
    if (cleanRegNo.length === 8) return '8-digit';
    if (cleanRegNo.length === 10) return '10-digit';
    return 'invalid';
  }
};

/**
 * Checks if registration number has reached the correct length for API validation
 * Triggers API calls when the input has at least 7 characters
 */
export const shouldTriggerApiCheck = (regNo: string): boolean => {
  // Trigger API check if the registration number has at least 7 characters
  return regNo.length >= 7;
};

 